import { ExternalLink, MoreHorizontal, Plus } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { PageHeader } from '../components/PageHeader';
import { Badge } from '../components/Badge';
import { Avatar } from '../components/Avatar';
import { useBusinesses } from '../api/hooks';
import { useBrand } from '../brand/BrandProvider';

export function Businesses() {
  const brand = useBrand();
  const navigate = useNavigate();
  const { data: businesses, isLoading } = useBusinesses();
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Account"
        title="Businesses"
        subtitle="Every shop, restaurant, or storefront under your account."
        action={
          <Button iconLeft={<Plus className="w-4 h-4" />} onClick={() => navigate({ to: '/businesses/new' })}>New business</Button>
        }
      />

      <Card className="overflow-hidden">
        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-700">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Business</th>
                <th className="text-left font-medium px-4 py-3">Plan</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Domain</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {isLoading || !businesses ? (
                <tr>
                  <td colSpan={5} className="px-5 sm:px-6 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    Loading businesses…
                  </td>
                </tr>
              ) : (
                businesses.map((b) => (
                <tr
                  key={b.id}
                  className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition cursor-pointer"
                >
                  <td className="px-5 sm:px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Avatar name={b.name} size="md" />
                      <div className="min-w-0">
                        <div className="font-medium text-zinc-950 dark:text-zinc-50 truncate">
                          {b.name}
                        </div>
                        <div className="text-xs text-zinc-500 dark:text-zinc-400">{b.subtitle}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="text-sm text-zinc-700 dark:text-zinc-200">{b.plan}</span>
                      {b.isPro && (
                        <span className="px-1.5 py-px rounded text-[10px] font-medium bg-zinc-950 text-white dark:bg-zinc-50 dark:text-zinc-950">
                          Pro
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <Badge tone={b.status === 'active' ? 'success' : b.status === 'trial' ? 'warning' : 'error'}>
                      {b.status === 'active' ? 'Active' : b.status === 'trial' ? 'Trial' : 'Past due'}
                    </Badge>
                  </td>
                  <td className="px-4 py-4 hidden md:table-cell">
                    <span className="mono text-xs text-zinc-600 dark:text-zinc-300">
                      {b.id.replace('biz_', '')}.{brand.domain}
                    </span>
                  </td>
                  <td className="px-5 sm:px-6 py-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                        title="Open POS"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                        title="More"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="mt-6 text-sm text-zinc-500 dark:text-zinc-400">
        Add another business if you operate multiple shops or companies. Each business gets its own subscription, its own POS, and (on Pro+) its own database.
      </p>
    </div>
  );
}
